import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Rocket, Eye, CheckCircle2 } from "lucide-react";
import { api, errMsg, fmtDate } from "@/lib/api";
import { useSite } from "@/context/SiteContext";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";

const LABELS = { services: "Layanan", portfolio: "Portofolio", articles: "Artikel", faqs: "FAQ", profile: "Profil Perusahaan", translations: "Terjemahan" };

export default function PublishBar() {
  const [st, setSt] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const { refresh } = useSite();
  const load = useCallback(() => api.get("/admin/publish").then((r) => setSt(r.data)).catch(() => {}), []);
  useEffect(() => {
    load();
    const id = setInterval(load, 4000);
    return () => clearInterval(id);
  }, [load]);
  const publish = async () => {
    setBusy(true);
    try {
      setSt((await api.post("/admin/publish")).data);
      refresh();
      toast.success("Perubahan berhasil dipublikasikan ke website publik");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
      setConfirm(false);
    }
  };
  if (!st) return null;
  const pending = st.pending > 0;
  return (
    <div className={`sticky top-0 z-30 flex flex-wrap items-center justify-between gap-3 border-b px-4 py-3 sm:px-8 ${pending ? "border-amber-200 bg-amber-50" : "border-slate-200 bg-white"}`} data-testid="publish-bar">
      <div className="text-sm">
        {pending ? (
          <p className="font-semibold text-amber-900" data-testid="publish-pending-text">
            {st.pending} perubahan belum dipublikasikan
            <span className="ml-2 font-normal text-amber-700">({(st.changed || []).map((c) => LABELS[c] || c).join(", ")})</span>
          </p>
        ) : (
          <p className="flex items-center gap-1.5 text-slate-600" data-testid="publish-uptodate-text"><CheckCircle2 size={14} className="text-emerald-600" /> Website publik sudah versi terbaru</p>
        )}
        <p className="font-mono text-[10px] text-slate-500">Terakhir dipublikasikan: {fmtDate(st.published_at)}{st.published_by ? ` oleh ${st.published_by}` : ""}</p>
      </div>
      <div className="flex gap-2">
        <a href="/?preview=1" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 border border-slate-300 bg-white px-3 py-2 text-xs font-semibold hover:border-[#0F2B48]" data-testid="publish-preview-button"><Eye size={14} /> Pratinjau</a>
        <button onClick={() => setConfirm(true)} disabled={!pending || busy} className="inline-flex items-center gap-1.5 bg-[#D97706] px-4 py-2 text-xs font-bold text-white transition-colors hover:bg-[#B45309] disabled:cursor-not-allowed disabled:opacity-40" data-testid="publish-button">
          <Rocket size={14} /> {busy ? "Mempublikasikan..." : "Publish ke Website"}
        </button>
      </div>
      <AlertDialog open={confirm} onOpenChange={setConfirm}>
        <AlertDialogContent className="rounded-none" data-testid="publish-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Publikasikan perubahan?</AlertDialogTitle>
            <AlertDialogDescription>{st.pending} perubahan ({(st.changed || []).map((c) => LABELS[c] || c).join(", ")}) akan langsung tampil di website publik untuk semua pengunjung.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-none" data-testid="publish-cancel-button">Batal</AlertDialogCancel>
            <AlertDialogAction onClick={publish} className="rounded-none bg-[#D97706] hover:bg-[#B45309]" data-testid="publish-confirm-button">Ya, Publish Sekarang</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
